import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons, CurrencyInput } from '../../shared/ui';
const { DollarSign, X, Building: Building2, Home, User, Calendar, CreditCard, Document: FileText, Spinner: Loader2, Upload, Image, Trash: Trash2 } = Icons;
import api from '../../utils/api';

const ManualPaymentModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation(['finance', 'common']);
  // State
  const [buildings, setBuildings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [paidAmount, setPaidAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [transactionReference, setTransactionReference] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [error, setError] = useState('');

  // Proof upload state
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Fetch buildings on mount
  useEffect(() => {
    if (isOpen) {
      fetchBuildings();
    }
  }, [isOpen]);

  // Fetch rooms when building changes
  useEffect(() => {
    if (selectedBuildingId) {
      fetchRoomsWithContracts(selectedBuildingId);
    } else {
      setRooms([]);
      setSelectedRoomId('');
      setSelectedRoom(null);
    }
  }, [selectedBuildingId]);

  // Payment info state for auto-populating final_amount
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loadingPayment, setLoadingPayment] = useState(false);

  // Update selected room details when room changes
  useEffect(() => {
    if (selectedRoomId) {
      const room = rooms.find(r => r.room_id === parseInt(selectedRoomId));
      setSelectedRoom(room);
    } else {
      setSelectedRoom(null);
      setPaymentInfo(null);
    }
  }, [selectedRoomId, rooms]);

  // Fetch payment info when room or month changes to get final_amount
  useEffect(() => {
    const fetchPaymentInfo = async () => {
      if (!selectedRoomId || !month) {
        setPaymentInfo(null);
        return;
      }

      try {
        setLoadingPayment(true);
        // Try to get existing payment for this room/month
        const response = await api.get('/api/admin/payments', {
          params: { room_id: selectedRoomId, month: month, limit: 1 }
        });
        const payments = response.data?.payments || response.data || [];
        if (payments.length > 0) {
          const payment = payments[0];
          setPaymentInfo(payment);
          // Pre-fill with final_amount (after adjustments) or invoice_amount
          const amount = payment.final_amount ?? payment.invoice_amount ?? 0;
          setPaidAmount(amount.toString());
        } else {
          setPaymentInfo(null);
          // Fall back to monthly rent if no payment found
          if (selectedRoom?.monthly_rent) {
            setPaidAmount(selectedRoom.monthly_rent.toString());
          }
        }
      } catch (err) {
        console.error('Error fetching payment info:', err);
        // Fall back to monthly rent
        if (selectedRoom?.monthly_rent) {
          setPaidAmount(selectedRoom.monthly_rent.toString());
        }
      } finally {
        setLoadingPayment(false);
      }
    };

    fetchPaymentInfo();
  }, [selectedRoomId, month, selectedRoom]);

  const fetchBuildings = async () => {
    try {
      const response = await api.get('/api/properties/buildings');
      setBuildings(Array.isArray(response.data) ? response.data : (response.data?.buildings || []));
    } catch (err) {
      console.error('Error fetching buildings:', err);
    }
  };

  const fetchRoomsWithContracts = async (buildingId) => {
    try {
      setLoadingRooms(true);
      const response = await api.get(`/api/admin/rooms/with-contracts?building_id=${buildingId}`);
      setRooms(response.data || []);
    } catch (err) {
      console.error('Error fetching rooms:', err);
      setRooms([]);
    } finally {
      setLoadingRooms(false);
    }
  };

  const resetForm = () => {
    setSelectedBuildingId('');
    setSelectedRoomId('');
    setSelectedRoom(null);
    setPaidAmount('');
    setTransactionReference('');
    setAdminNote('');
    setPaymentMethod('cash');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    const now = new Date();
    setMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
    setError('');
    // Reset proof
    setProofFile(null);
    setProofPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle proof file selection
  const handleProofFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError(t('finance:manualPayment.error.imageRequired'));
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError(t('finance:manualPayment.error.fileTooLarge'));
        return;
      }
      setProofFile(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setProofPreview(previewUrl);
      setError('');
    }
  };

  // Remove proof file
  const removeProofFile = () => {
    setProofFile(null);
    if (proofPreview) {
      URL.revokeObjectURL(proofPreview);
    }
    setProofPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedRoomId || !month || !paidAmount) {
      setError(t('finance:manualPayment.error.requiredFields'));
      return;
    }

    const amount = parseFloat(paidAmount);
    if (isNaN(amount) || amount <= 0) {
      setError(t('finance:manualPayment.error.amountPositive'));
      return;
    }

    try {
      setLoading(true);

      // Use FormData to support file upload
      const formData = new FormData();
      formData.append('room_id', selectedRoomId);
      formData.append('month', month);
      formData.append('paid_amount', amount.toString());
      formData.append('payment_method', paymentMethod);
      formData.append('payment_date', paymentDate);
      if (transactionReference) {
        formData.append('transaction_reference', transactionReference);
      }
      if (adminNote) {
        formData.append('admin_note', adminNote);
      }
      if (proofFile) {
        formData.append('proof_image', proofFile);
      }

      const response = await api.post('/api/admin/payments/manual', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      onSuccess?.(response.data);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.detail || t('finance:manualPayment.error.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    return Number(value).toLocaleString('vi-VN');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-stone-800 rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <DollarSign className="h-6 w-6 text-green-600 mr-2" />
            <h2 className="text-xl font-semibold text-stone-900 dark:text-white">{t('finance:manualPayment.title')}</h2>
          </div>
          <button onClick={handleClose} className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-300">
            <X className="h-6 w-6" />
          </button>
        </div>

        <p className="text-sm text-stone-600 dark:text-stone-400 mb-4">
          {t('finance:manualPayment.subtitle')}
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-md text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Building Select */}
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
              <Building2 className="h-4 w-4 inline mr-1" />
              {t('finance:manualPayment.field.building')} *
            </label>
            <select
              value={selectedBuildingId}
              onChange={(e) => setSelectedBuildingId(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-md focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-stone-700 text-stone-900 dark:text-white"
              required
            >
              <option value="">{t('finance:manualPayment.select.building')}</option>
              {buildings.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          {/* Room Select */}
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
              <Home className="h-4 w-4 inline mr-1" />
              {t('finance:manualPayment.field.room')} *
            </label>
            <select
              value={selectedRoomId}
              onChange={(e) => setSelectedRoomId(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-md focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-stone-700 text-stone-900 dark:text-white disabled:bg-stone-100 dark:disabled:bg-stone-600"
              required
              disabled={!selectedBuildingId || loadingRooms}
            >
              <option value="">
                {loadingRooms ? t('common:status.loading') : rooms.length === 0 && selectedBuildingId ? t('finance:manualPayment.select.noRooms') : t('finance:manualPayment.select.room')}
              </option>
              {rooms.map(r => (
                <option key={r.room_id} value={r.room_id}>
                  {r.room_number} - {r.renter_name} ({formatCurrency(r.monthly_rent)}{t('finance:manualPayment.info.perMonth')})
                </option>
              ))}
            </select>
            {selectedRoom && (
              <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
                <User className="h-3 w-3 inline mr-1" />
                {t('finance:manualPayment.info.tenant')} {selectedRoom.renter_name}
              </p>
            )}
          </div>

          {/* Month */}
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
              <Calendar className="h-4 w-4 inline mr-1" />
              {t('finance:manualPayment.field.paymentMonth')} *
            </label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-md focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-stone-700 text-stone-900 dark:text-white"
              required
            />
          </div>

          {/* Payment Info Display */}
          {loadingPayment && (
            <div className="p-3 bg-stone-50 dark:bg-stone-700 rounded-md text-sm text-stone-500">
              <Loader2 className="h-4 w-4 inline mr-2 animate-spin" />
              {t('finance:manualPayment.info.loadingPayment')}
            </div>
          )}

          {paymentInfo && !loadingPayment && (
            <div className="p-3 bg-teal-50 dark:bg-teal-900/30 rounded-md text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-stone-600 dark:text-stone-400">{t('finance:manualPayment.info.invoice')}</span>
                <span className="font-medium">₫{formatCurrency(paymentInfo.invoice_amount)}</span>
              </div>
              {paymentInfo.final_amount != null && paymentInfo.final_amount !== paymentInfo.invoice_amount && (
                <>
                  <div className="flex justify-between text-green-600">
                    <span>{t('finance:manualPayment.info.adjustment')}</span>
                    <span>-₫{formatCurrency(paymentInfo.invoice_amount - paymentInfo.final_amount)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-teal-700 dark:text-teal-400 border-t border-teal-200 pt-1">
                    <span>{t('finance:manualPayment.info.finalAmount')}</span>
                    <span>₫{formatCurrency(paymentInfo.final_amount)}</span>
                  </div>
                </>
              )}
              {paymentInfo.paid_amount > 0 && (
                <div className="flex justify-between text-amber-600">
                  <span>{t('finance:manualPayment.info.alreadyPaid')}</span>
                  <span>₫{formatCurrency(paymentInfo.paid_amount)}</span>
                </div>
              )}
            </div>
          )}

          {/* Paid Amount */}
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
              {t('finance:manualPayment.field.amount')} *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-500 dark:text-stone-400">₫</span>
              <CurrencyInput
                value={paidAmount}
                onChange={(e) => setPaidAmount(e.target.value)}
                className="w-full pl-8 pr-3 py-2 border border-stone-300 dark:border-stone-600 rounded-md focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-stone-700 text-stone-900 dark:text-white"
                placeholder={t('finance:manualPayment.placeholder.amount')}
                required
              />
            </div>
            {!paymentInfo && selectedRoom?.monthly_rent && (
              <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
                {t('finance:manualPayment.info.monthlyRent')} {formatCurrency(selectedRoom.monthly_rent)}{t('finance:manualPayment.info.perMonth')}
              </p>
            )}
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
              <CreditCard className="h-4 w-4 inline mr-1" />
              {t('finance:manualPayment.field.method')} *
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-md focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-stone-700 text-stone-900 dark:text-white"
              required
            >
              <option value="cash">{t('common:payment.method.cash')}</option>
              <option value="bank_transfer">{t('common:payment.method.transfer')}</option>
              <option value="momo">{t('common:payment.method.momo')}</option>
              <option value="zalo_pay">{t('common:payment.method.zalopay')}</option>
              <option value="other">{t('common:payment.method.other')}</option>
            </select>
          </div>

          {/* Payment Date */}
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
              {t('finance:manualPayment.field.paymentDate')}
            </label>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-md focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-stone-700 text-stone-900 dark:text-white"
            />
          </div>

          {/* Transaction Reference */}
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
              {t('finance:manualPayment.field.transactionRef')}
            </label>
            <input
              type="text"
              value={transactionReference}
              onChange={(e) => setTransactionReference(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-md focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-stone-700 text-stone-900 dark:text-white"
              placeholder={t('finance:manualPayment.placeholder.transactionRef')}
            />
          </div>

          {/* Admin Note */}
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
              <FileText className="h-4 w-4 inline mr-1" />
              {t('finance:manualPayment.field.notes')}
            </label>
            <textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 dark:border-stone-600 rounded-md focus:ring-teal-500 focus:border-teal-500 bg-white dark:bg-stone-700 text-stone-900 dark:text-white"
              rows={2}
              placeholder={t('finance:manualPayment.placeholder.notes')}
            />
          </div>

          {/* Proof Upload */}
          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
              <Image className="h-4 w-4 inline mr-1" />
              {t('finance:manualPayment.field.proof')}
            </label>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleProofFileChange}
              accept="image/*"
              className="hidden"
            />
            {!proofPreview ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full px-4 py-6 border-2 border-dashed border-stone-300 dark:border-stone-600 rounded-lg hover:border-teal-400 dark:hover:border-teal-500 transition-colors flex flex-col items-center justify-center gap-2 text-stone-500 dark:text-stone-400 hover:text-teal-500"
              >
                <Upload className="h-8 w-8" />
                <span className="text-sm">{t('finance:manualPayment.proof.upload')}</span>
                <span className="text-xs text-stone-400">{t('finance:manualPayment.proof.hint')}</span>
              </button>
            ) : (
              <div className="relative border border-stone-300 dark:border-stone-600 rounded-lg overflow-hidden">
                <img
                  src={proofPreview}
                  alt="Payment proof preview"
                  className="w-full h-48 object-contain bg-stone-100 dark:bg-stone-700"
                />
                <div className="absolute top-2 right-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 bg-teal-600 text-white rounded-full hover:bg-teal-700 shadow-lg"
                    title={t('finance:manualPayment.proof.change')}
                  >
                    <Upload className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={removeProofFile}
                    className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 shadow-lg"
                    title={t('finance:manualPayment.proof.remove')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="p-2 bg-stone-50 dark:bg-stone-700 text-xs text-stone-600 dark:text-stone-400 truncate">
                  {proofFile?.name}
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t dark:border-stone-700">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-stone-300 dark:border-stone-600 rounded-md text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700"
              disabled={loading}
            >
              {t('common:action.cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
              disabled={loading || !selectedRoomId}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('finance:manualPayment.action.creating')}
                </>
              ) : (
                t('finance:manualPayment.action.create')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualPaymentModal;
